class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    if (FacetFiltersForm.historyListenerSet) return;

    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
    FacetFiltersForm.historyListenerSet = true;
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.abortController?.abort();
    FacetFiltersForm.abortController = new AbortController();
    const { signal } = FacetFiltersForm.abortController;
    const renderRevision = ++FacetFiltersForm.renderRevision;
    const fallbackURL = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const productGridContainer = document.getElementById('ProductGridContainer');

    if (!productGridContainer) {
      FacetFiltersForm.navigateToFallback(fallbackURL, renderRevision);
      return;
    }

    productGridContainer.querySelector('.collection')?.classList.add('loading');
    productGridContainer.setAttribute('aria-busy', 'true');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    let sections;

    try {
      sections = FacetFiltersForm.getSections();
    } catch (error) {
      FacetFiltersForm.navigateToFallback(fallbackURL, renderRevision);
      return;
    }

    const renderRequests = sections.map((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      return FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, renderRevision)
        : FacetFiltersForm.renderSectionFromFetch(url, event, renderRevision, signal);
    });

    Promise.all(renderRequests)
      .then(() => {
        if (renderRevision !== FacetFiltersForm.renderRevision || signal.aborted) return;
        FacetFiltersForm.clearLoadingState();
      })
      .catch((error) => {
        if (signal.aborted || error?.name === 'AbortError') return;
        FacetFiltersForm.navigateToFallback(fallbackURL, renderRevision);
      });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, renderRevision, signal) {
    return fetch(url, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Facet request failed (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        if (signal.aborted || renderRevision !== FacetFiltersForm.renderRevision) return;

        const html = responseText;
        FacetFiltersForm.renderSection(html, event, renderRevision);
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData.filter((filterData) => filterData.url !== url),
          { html, url }
        ];
      });
  }

  static renderSectionFromCache(filterDataUrl, event, renderRevision) {
    return Promise.resolve().then(() => {
      if (renderRevision !== FacetFiltersForm.renderRevision) return;

      const cachedSection = FacetFiltersForm.filterData.find(filterDataUrl);
      if (!cachedSection) throw new Error('Cached facet response is unavailable');
      FacetFiltersForm.renderSection(cachedSection.html, event, renderRevision);
    });
  }

  static renderSection(html, event, renderRevision) {
    if (renderRevision !== FacetFiltersForm.renderRevision) return;

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceGrid = parsedHTML.getElementById('ProductGridContainer');
    const sourceCount = parsedHTML.getElementById('ProductCount');

    if (!sourceGrid || !sourceCount) {
      throw new Error('Facet response is missing required collection markup');
    }

    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(sourceGrid);
    FacetFiltersForm.renderProductCount(sourceCount);
  }

  static renderProductGridContainer(sourceGrid) {
    const targetGrid = document.getElementById('ProductGridContainer');
    if (!targetGrid) throw new Error('Product grid target is unavailable');
    targetGrid.innerHTML = sourceGrid.innerHTML;
  }

  static renderProductCount(sourceCount) {
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (!container) throw new Error('Product count target is unavailable');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event?.target?.closest?.('.js-filter');
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    const currentFilter = event?.target?.closest?.('.js-filter');
    if (countsToRender && currentFilter) FacetFiltersForm.renderCounts(countsToRender, currentFilter);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    const menuDrawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
    if (menuDrawer) FacetFiltersForm.bindNewMenuDrawerControls(menuDrawer);
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static clearLoadingState() {
    const productGridContainer = document.getElementById('ProductGridContainer');
    productGridContainer?.querySelector('.collection')?.classList.remove('loading');
    productGridContainer?.removeAttribute('aria-busy');
    document.getElementById('ProductCount')?.classList.remove('loading');
    document.getElementById('ProductCountDesktop')?.classList.remove('loading');
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static navigateToFallback(url, renderRevision) {
    if (renderRevision !== FacetFiltersForm.renderRevision) return;
    FacetFiltersForm.clearLoadingState();
    window.location.assign(url);
  }

  static rememberExistingMenuDrawerControls() {
    document.querySelectorAll('#FacetFiltersFormMobile').forEach((mobileForm) => {
      const menuDrawer = mobileForm.closest('menu-drawer');
      if (!menuDrawer) return;

      menuDrawer.querySelectorAll('summary').forEach((summary) => {
        FacetFiltersForm.boundMenuDrawerSummaries.add(summary);
      });
      menuDrawer.querySelectorAll('button').forEach((button) => {
        FacetFiltersForm.boundMenuDrawerButtons.add(button);
      });
    });
  }

  static bindNewMenuDrawerControls(menuDrawer) {
    menuDrawer.querySelectorAll('summary').forEach((summary) => {
      if (FacetFiltersForm.boundMenuDrawerSummaries.has(summary)) return;
      summary.addEventListener('click', menuDrawer.onSummaryClick.bind(menuDrawer));
      FacetFiltersForm.boundMenuDrawerSummaries.add(summary);
    });

    menuDrawer.querySelectorAll('button').forEach((button) => {
      if (FacetFiltersForm.boundMenuDrawerButtons.has(button)) return;
      button.addEventListener('click', menuDrawer.onCloseButtonClick.bind(menuDrawer));
      FacetFiltersForm.boundMenuDrawerButtons.add(button);
    });
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid?.dataset.id) throw new Error('Product grid section identifier is unavailable');

    return [
      {
        section: productGrid.dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.abortController = null;
FacetFiltersForm.renderRevision = 0;
FacetFiltersForm.historyListenerSet = false;
FacetFiltersForm.boundMenuDrawerSummaries = new WeakSet();
FacetFiltersForm.boundMenuDrawerButtons = new WeakSet();
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.rememberExistingMenuDrawerControls();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
